import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory } from 'react-router-dom';
import { setKanbanLaneOrder, getKanbanLaneOrder } from '../../services/companyKanbanService';
import { Button, TextField, Paper, FormControl, InputLabel, Select, FormControlLabel, Switch, Typography } from '@material-ui/core';
import { format } from 'date-fns';
import { Can } from '../../components/Can';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import KanbanBoard from './KanbanBoard';

const useStyles = makeStyles(theme => ({
  /** Garante texto legível em inputs outlined no modo escuro */
  outlinedFieldContrast: {
    '& .MuiOutlinedInput-root': {
      backgroundColor:
        theme.palette.type === 'dark' ? theme.palette.background.paper : undefined,
      '& .MuiOutlinedInput-input': {
        color: theme.palette.text.primary,
      },
      '& .MuiSelect-select': {
        color: theme.palette.text.primary,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor:
          theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.22)' : undefined,
      },
    },
    '& .MuiInputLabel-outlined': {
      color: theme.palette.text.secondary,
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
  mainPaper: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    padding: theme.spacing(1),
    overflow: 'hidden',
    borderRadius: '10px',
    position: 'relative',
  },
  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(0, 0, 0, 0.58)' : 'rgba(255, 255, 255, 0.72)',
    backdropFilter: 'blur(1px)',
  },
  loadingCard: {
    minWidth: 320,
    padding: theme.spacing(2),
    borderRadius: 12,
    background:
      theme.palette.type === 'dark' ? 'rgba(31, 41, 55, 0.96)' : 'rgba(255, 255, 255, 0.96)',
    border:
      theme.palette.type === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.15)'
        : '1px solid rgba(15, 23, 42, 0.08)',
    boxShadow:
      theme.palette.type === 'dark'
        ? '0 14px 32px rgba(0, 0, 0, 0.55)'
        : '0 14px 28px rgba(15, 23, 42, 0.14)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1),
  },
  loadingText: {
    fontWeight: 600,
    fontSize: '0.92rem',
    color: theme.palette.text.primary,
  },
  loadingMeta: {
    fontSize: '0.78rem',
    color: theme.palette.text.secondary,
  },
  progressTrack: {
    width: '100%',
    height: 8,
    borderRadius: 999,
    overflow: 'hidden',
    background:
      theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.16)'
        : 'rgba(15, 23, 42, 0.12)',
  },
  progressFill: {
    height: '100%',
    borderRadius: 999,
    background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
    transition: 'width 160ms ease',
  },
  progressHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  progressPercent: {
    fontWeight: 700,
    fontSize: '0.84rem',
    color: theme.palette.text.primary,
    minWidth: 42,
    textAlign: 'right',
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  button: {
    borderRadius: '10px',
    minWidth: 84,
    padding: '4px 10px',
    fontSize: '0.8rem',
  },
  dateInput: {
    width: 132,
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    marginRight: theme.spacing(0.5),
  },
  sortSelect: {
    minWidth: 220,
    width: 260,
    marginRight: theme.spacing(0.5),
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      backgroundColor: theme.palette.background.paper,
      paddingLeft: 4,
      paddingRight: 4,
    },
  },
  switchLabel: {
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    '& .MuiFormControlLabel-label': {
      fontSize: '0.85rem',
      color: theme.palette.text.primary,
    },
  },
  filterSelect: {
    width: 170,
    marginRight: theme.spacing(0.5),
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    '& .MuiInputLabel-root': {
      whiteSpace: 'nowrap',
      fontSize: '0.92rem',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      backgroundColor: theme.palette.background.paper,
      paddingLeft: 4,
      paddingRight: 4,
    },
  },
  filtersCompact: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '& > *': {
      margin: 0,
    },
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [filterUserId, setFilterUserId] = useState('');
  const [filterConnectionId, setFilterConnectionId] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [connectionsList, setConnectionsList] = useState([]);
  const queueIds = user.queues.map(queue => queue.UserQueue.queueId);

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('sortOrder') || 'ticketNumber';
  });

  const [laneOrder, setLaneOrder] = useState(null);
  const [loadingLaneOrder, setLoadingLaneOrder] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  const [boardLoading, setBoardLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({
    loaded: 0,
    total: null,
    percent: 0,
  });

  const canSeeOtherUsersChats = user?.profile === 'admin' || user?.allUserChat === 'enabled';
  const usersListForFilter = useMemo(() => {
    if (canSeeOtherUsersChats) return usersList;
    const onlyMe = usersList.filter(u => Number(u.id) === Number(user?.id));
    return onlyMe.length > 0 ? onlyMe : (user?.id && user?.name ? [{ id: user.id, name: user.name }] : []);
  }, [canSeeOtherUsersChats, usersList, user?.id, user?.name]);

  useEffect(() => {
    if (filterUserId && !usersListForFilter.some(u => Number(u.id) === Number(filterUserId))) {
      setFilterUserId('');
    }
  }, [usersListForFilter, filterUserId]);

  useEffect(() => {
    localStorage.setItem('sortOrder', sortOrder);
  }, [sortOrder]);

  // Carregar ordem das lanes do banco de dados
  useEffect(() => {
    const loadLaneOrder = async () => {
      try {
        const savedOrder = await getKanbanLaneOrder();
        setLaneOrder(savedOrder);
      } catch (error) {
        console.error('Erro ao carregar ordem das lanes:', error);
      } finally {
        setLoadingLaneOrder(false);
      }
    };

    if (user && user.id) {
      loadLaneOrder();
    }
  }, [user]);

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadUsersAndConnections = async () => {
      try {
        const [usersRes, whatsappRes] = await Promise.all([
          api.get('/users/list'),
          api.get('/whatsapp/'),
        ]);
        const list = usersRes.data && Array.isArray(usersRes.data) ? usersRes.data : [];
        setUsersList(list.map(u => ({ id: u.id, name: u.name })));
        const conns = whatsappRes.data && Array.isArray(whatsappRes.data) ? whatsappRes.data : [];
        const companyConnections = conns.filter(c => c.companyId === user.companyId);
        setConnectionsList(companyConnections.map(c => ({ id: c.id, name: c.name })));
      } catch (err) {
        console.error(err);
      }
    };
    if (user && user.companyId) loadUsersAndConnections();
  }, [user]);

  const fetchTags = async () => {
    try {
      const response = await api.get('/tag/kanban/');
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets(fetchedTags);
    } catch (error) {
      console.log(error);
    }
  };

  // Limite de páginas para evitar sobrecarga (ex.: 15 páginas = 6000 tickets)
  const MAX_KANBAN_PAGES = 15;
  const FETCH_RETRY_COOLDOWN_MS = 4000;
  const isFetchingRef = useRef(false);
  const pendingFetchRef = useRef(false);
  const fetchSequenceRef = useRef(0);
  const lastFetchErrorAtRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  const fetchTickets = async (fetchedTags = tags, options = {}) => {
    const { source = 'manual', force = false } = options;
    const shouldShowLoadingOverlay =
      !hasLoadedOnceRef.current || source === 'manual' || source === 'queued';

    if (isFetchingRef.current) {
      pendingFetchRef.current = true;
      return;
    }

    const now = Date.now();
    if (
      !force &&
      source === 'socket' &&
      lastFetchErrorAtRef.current &&
      now - lastFetchErrorAtRef.current < FETCH_RETRY_COOLDOWN_MS
    ) {
      return;
    }

    isFetchingRef.current = true;
    const currentFetchSequence = ++fetchSequenceRef.current;
    if (shouldShowLoadingOverlay) {
      setBoardLoading(true);
      setLoadingProgress({ loaded: 0, total: null, percent: 0 });
    }

    try {
      const params = {
        queueIds: JSON.stringify(queueIds),
      };
      // Só aplicar filtro de data quando estiver vendo fechados (senão carrega todos os abertos, qualquer data)
      if (showClosed) {
        params.startDate = startDate;
        params.endDate = endDate;
        params.dateStart = startDate;
        params.dateEnd = endDate;
        params.showClosed = 'true';
      }
      if (filterUserId) params.users = JSON.stringify([Number(filterUserId)]);
      if (filterConnectionId) params.whatsapps = JSON.stringify([Number(filterConnectionId)]);

      let allTickets = [];
      let pageNumber = 1;
      let hasMore = true;
      let lastKnownTotal = null;

      while (hasMore && pageNumber <= MAX_KANBAN_PAGES) {
        const { data } = await api.get('/ticket/kanban', { params: { ...params, pageNumber: String(pageNumber) } });
        allTickets = allTickets.concat(data.tickets || []);
        if (shouldShowLoadingOverlay) {
          const totalCount = Number(data?.count);
          const hasTotal = Number.isFinite(totalCount) && totalCount > 0;
          if (hasTotal) lastKnownTotal = totalCount;
          const loadedCount = allTickets.length;
          const rawPercent = hasTotal ? Math.round((loadedCount / totalCount) * 100) : 0;
          const safePercent = hasTotal
            ? Math.max(1, Math.min(100, rawPercent))
            : Math.min(95, pageNumber * 8);

          setLoadingProgress({
            loaded: loadedCount,
            total: hasTotal ? totalCount : null,
            percent: safePercent,
          });
        }
        hasMore = data.hasMore === true && (data.tickets || []).length > 0;
        pageNumber += 1;
      }

      if (currentFetchSequence !== fetchSequenceRef.current) {
        return;
      }

      setTickets(allTickets);
      organizeLanes(fetchedTags, allTickets);
      hasLoadedOnceRef.current = true;
      if (shouldShowLoadingOverlay) {
        const finalTotal = lastKnownTotal && lastKnownTotal > 0 ? lastKnownTotal : allTickets.length;
        setLoadingProgress({
          loaded: allTickets.length,
          total: finalTotal,
          percent: 100,
        });
      }
    } catch (err) {
      console.log(err);
      lastFetchErrorAtRef.current = Date.now();
    } finally {
      if (currentFetchSequence === fetchSequenceRef.current) {
        setBoardLoading(false);
      }
      isFetchingRef.current = false;
      if (pendingFetchRef.current) {
        pendingFetchRef.current = false;
        // Executa somente o último refresh pendente para evitar tempestade.
        fetchTickets(undefined, { source: 'queued', force: true });
      }
    }
  };

  const debounceRef = useRef(null);
  const tagsRef = useRef(tags);
  const ticketsRef = useRef(tickets);
  const fetchTicketsRef = useRef(fetchTickets);
  const canDisplayTicketRef = useRef(() => true);

  useEffect(() => {
    tagsRef.current = tags;
  }, [tags]);

  useEffect(() => {
    ticketsRef.current = tickets;
  }, [tickets]);

  useEffect(() => {
    fetchTicketsRef.current = fetchTickets;
  }, [fetchTickets]);

  const canDisplayTicket = useCallback((ticket) => {
    if (!ticket) return false;

    const isAdmin = user?.profile === 'admin';
    const showTicketWithoutQueue = ['enable', 'enabled'].includes(
      String(user?.allTicket || '').toLowerCase()
    );

    const ticketStatus = String(ticket.status || '').toLowerCase();
    const ticketQueueId =
      ticket?.queueId !== undefined && ticket?.queueId !== null
        ? Number(ticket.queueId)
        : (ticket?.queue?.id !== undefined && ticket?.queue?.id !== null ? Number(ticket.queue.id) : null);
    const ticketUserId =
      ticket?.userId !== undefined && ticket?.userId !== null ? Number(ticket.userId) : null;
    const currentUserId = Number(user?.id);

    // Mesmo filtro de status usado no fetch atual do Kanban.
    const visibleStatuses = showClosed ? ['pending', 'open', 'closed'] : ['pending', 'open'];
    if (!visibleStatuses.includes(ticketStatus)) return false;

    // Regras de fila / sem fila.
    if (ticketQueueId === null) {
      if (!showTicketWithoutQueue) return false;
    } else if (!queueIds.includes(ticketQueueId)) {
      return false;
    }

    // Regras de dono/pendente para não-admin (paridade com ListTicketsServiceKanban sem showAll).
    if (!isAdmin) {
      const isOwnTicket = ticketUserId === currentUserId;
      const isPendingUnassigned = ticketStatus === 'pending' && ticketUserId === null;

      // Sem showAll ativo no Kanban, usuário comum só vê próprio ticket ou pending sem usuário.
      if (!(isOwnTicket || isPendingUnassigned)) {
        return false;
      }
    }

    // Filtros de tela ativos.
    if (filterUserId && ticketUserId !== Number(filterUserId)) return false;
    if (filterConnectionId) {
      const ticketWhatsappId =
        ticket?.whatsappId !== undefined && ticket?.whatsappId !== null
          ? Number(ticket.whatsappId)
          : (ticket?.whatsapp?.id !== undefined && ticket?.whatsapp?.id !== null ? Number(ticket.whatsapp.id) : null);
      if (ticketWhatsappId !== Number(filterConnectionId)) return false;
    }

    return true;
  }, [user, queueIds, showClosed, filterUserId, filterConnectionId]);

  useEffect(() => {
    canDisplayTicketRef.current = canDisplayTicket;
  }, [canDisplayTicket]);

  useEffect(() => {
    const companyId = user.companyId;

    const onAppMessage = data => {
      if (!['create', 'update', 'delete'].includes(data.action)) return;

      const socketTicket = data?.ticket;
      const socketTicketId = Number(data?.ticketId || socketTicket?.id);

      if (data.action === 'delete' && Number.isFinite(socketTicketId)) {
        setTickets(prevTickets => prevTickets.filter(t => Number(t.id) !== socketTicketId));
        return;
      }

      if ((data.action === 'create' || data.action === 'update') && socketTicket && Number.isFinite(socketTicketId)) {
        setTickets(prevTickets => {
          const isVisible = canDisplayTicketRef.current(socketTicket);
          if (!isVisible) {
            // Se não é mais visível pelas permissões/filtros, remove caso exista.
            return prevTickets.filter(t => Number(t.id) !== socketTicketId);
          }

          const idx = prevTickets.findIndex(t => Number(t.id) === socketTicketId);
          if (idx === -1) return [socketTicket, ...prevTickets];
          const next = [...prevTickets];
          next[idx] = { ...next[idx], ...socketTicket };
          return next;
        });
        return;
      }

      // Debounce: evita múltiplos reloads seguidos (ex.: vários tickets atualizados em sequência)
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        fetchTicketsRef.current(undefined, { source: 'socket' });
      }, 2000);
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.companyId]);

  const handleSearchClick = () => {
    fetchTickets(undefined, { source: 'manual', force: true });
  };

  const handleStartDateChange = event => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = event => {
    setEndDate(event.target.value);
  };

  const updateTicket = useCallback((updatedTicket) => {
    setTickets(prevTickets =>
      prevTickets.map(ticket =>
        ticket.id === updatedTicket.id ? updatedTicket : ticket
      )
    );
  }, []);

  const onTicketClosed = useCallback((ticketId) => {
    setTickets(prevTickets => prevTickets.filter(t => t.id !== ticketId));
  }, []);

  const getOpportunityValue = (ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return opportunityValue;
  };

  const organizeLanes = useCallback((fetchedTags = tagsRef.current, fetchedTickets = ticketsRef.current) => {
    const sortedTickets = [...fetchedTickets];

    if (sortOrder === 'ticketNumber') {
      sortedTickets.sort((a, b) => a.id - b.id);
    } else if (sortOrder === 'lastMessageTime') {
      sortedTickets.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    } else if (sortOrder === 'valorDesc') {
      sortedTickets.sort((a, b) => {
        const valorA = getOpportunityValue(a);
        const valorB = getOpportunityValue(b);
        return valorB - valorA;
      });
    }

    const defaultTickets = sortedTickets.filter(
      ticket => ticket.tags.length === 0
    );

    const lanesData = [
      {
        id: 'lane0',
        title: i18n.t('tagsKanban.laneDefault'),
        tickets: defaultTickets,
        color: '#757575',
      },
      ...fetchedTags.map(tag => {
        const taggedTickets = sortedTickets.filter(ticket =>
          ticket.tags.some(t => t.id === tag.id)
        );
        return {
          id: tag.id.toString(),
          title: tag.name,
          tickets: taggedTickets,
          color: tag.color || '#757575',
        };
      }),
    ];

    // Aplicar ordem personalizada se existir
    if (laneOrder && laneOrder.length > 0) {
      const orderedLanes = [];
      const laneMap = new Map(lanesData.map(lane => [lane.id, lane]));
      
      // Adicionar lanes na ordem salva
      laneOrder.forEach(laneId => {
        if (laneMap.has(laneId)) {
          orderedLanes.push(laneMap.get(laneId));
          laneMap.delete(laneId);
        }
      });
      
      // Adicionar lanes restantes (novas lanes que não estavam na ordem salva)
      laneMap.forEach(lane => orderedLanes.push(lane));
      
      setLanes(orderedLanes);
    } else {
      setLanes(lanesData);
    }
  }, [sortOrder, laneOrder]);

  useEffect(() => {
    if (!loadingLaneOrder) {
      organizeLanes(tags, tickets);
    }
  }, [tags, tickets, loadingLaneOrder, organizeLanes]);

  const applyTicketLaneLocally = useCallback((currentTickets, ticketId, targetLaneId) => {
    const numericTicketId = Number(ticketId);
    if (!Number.isFinite(numericTicketId)) return currentTickets;

    const targetTag =
      targetLaneId === 'lane0'
        ? null
        : (tagsRef.current || []).find(tag => String(tag.id) === String(targetLaneId));

    return currentTickets.map(ticket => {
      if (Number(ticket.id) !== numericTicketId) return ticket;
      return {
        ...ticket,
        tags: targetTag ? [targetTag] : [],
      };
    });
  }, []);

  const handleCardMove = useCallback(async (ticketId, targetLaneId, sourceLaneId) => {
    const numericTicketId = parseInt(ticketId, 10);
    if (targetLaneId === sourceLaneId) return;

    const previousTicket = ticketsRef.current.find(t => Number(t.id) === numericTicketId);
    const previousTags = Array.isArray(previousTicket?.tags) ? previousTicket.tags : [];

    setTickets(prevTickets => applyTicketLaneLocally(prevTickets, numericTicketId, targetLaneId));

    try {
      await api.delete(`/ticket-tags/${numericTicketId}`);

      if (targetLaneId !== 'lane0') {
        await api.put(`/ticket-tags/${numericTicketId}/${targetLaneId}`);
      }
    } catch (err) {
      // Rollback pontual somente do card que falhou.
      setTickets(prevTickets =>
        prevTickets.map(ticket =>
          Number(ticket.id) === numericTicketId
            ? { ...ticket, tags: previousTags }
            : ticket
        )
      );
      toast.error('Não foi possível mover o card. Tente novamente.');
      console.log(err);
    }
  }, [applyTicketLaneLocally]);

  const handleTicketTransferred = useCallback((ticketId) => {
    // Evita recarga imediata e pesada; deixa o socket aplicar patch incremental.
    // Fallback leve e diferido para garantir consistência eventual.
    window.setTimeout(() => {
      fetchTickets(undefined, { source: 'deferred' });
    }, 2500);
  }, [fetchTickets]);

  const handleAddColumnClick = () => {
    history.push('/tagsKanban');
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
  };

  const handleLaneReorder = async (sourceIndex, destinationIndex) => {
    // Verificar se o usuário é admin
    if (user.profile !== 'admin') {
      toast.error('Apenas administradores podem reordenar as lanes do Kanban');
      return;
    }

    const newLanes = Array.from(lanes);
    const [reorderedLane] = newLanes.splice(sourceIndex, 1);
    newLanes.splice(destinationIndex, 0, reorderedLane);
    
    setLanes(newLanes);
    
    // Salvar nova ordem no banco de dados
    const newLaneOrder = newLanes.map(lane => lane.id);
    setLaneOrder(newLaneOrder);
    
    try {
      await setKanbanLaneOrder(newLaneOrder);
      toast.success('Ordem das lanes atualizada com sucesso');
    } catch (error) {
      console.error('Erro ao salvar ordem das lanes:', error);
      toast.error('Erro ao salvar ordem das lanes');
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t('Kanban')}</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.filtersCompact}>
            <FormControl
              variant="outlined"
              size="small"
              className={`${classes.filterSelect} ${classes.outlinedFieldContrast}`}
            >
              <InputLabel htmlFor="filter-user-select" shrink>{i18n.t('kanban.filterByUser')}</InputLabel>
              <Select
                native
                value={filterUserId}
                onChange={(e) => setFilterUserId(e.target.value)}
                label={i18n.t('kanban.filterByUser')}
                inputProps={{ name: 'filterUser', id: 'filter-user-select' }}
              >
                <option value="">{i18n.t('kanban.filterAll')}</option>
                {usersListForFilter.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              size="small"
              className={`${classes.filterSelect} ${classes.outlinedFieldContrast}`}
            >
              <InputLabel htmlFor="filter-connection-select" shrink>{i18n.t('kanban.filterByConnection')}</InputLabel>
              <Select
                native
                value={filterConnectionId}
                onChange={(e) => setFilterConnectionId(e.target.value)}
                label={i18n.t('kanban.filterByConnection')}
                inputProps={{ name: 'filterConnection', id: 'filter-connection-select' }}
              >
                <option value="">{i18n.t('kanban.filterAll')}</option>
                {connectionsList.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </FormControl>
            <FormControl
              variant="outlined"
              size="small"
              className={`${classes.sortSelect} ${classes.outlinedFieldContrast}`}
            >
              <InputLabel htmlFor="sort-order-select" shrink>Ordenar por</InputLabel>
              <Select
                native
                value={sortOrder}
                onChange={handleSortOrderChange}
                label="Ordenar por"
                inputProps={{
                  name: 'sortOrder',
                  id: 'sort-order-select',
                }}
              >
                <option value="ticketNumber">Número do Ticket</option>
                <option value="lastMessageTime">Última Mensagem</option>
                <option value="valorDesc">Valor (maior para menor)</option>
              </Select>
            </FormControl>
            <TextField
              label="Data de início"
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              className={`${classes.dateInput} ${classes.outlinedFieldContrast}`}
              size="small"
            />
            <TextField
              label="Data de fim"
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              className={`${classes.dateInput} ${classes.outlinedFieldContrast}`}
              size="small"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showClosed}
                  onChange={(e) => setShowClosed(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={i18n.t('kanban.showClosed')}
              className={classes.switchLabel}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearchClick}
              className={classes.button}
            >
              Buscar
            </Button>
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddColumnClick}
                  className={classes.button}
                >
                  + Adicionar colunas
                </Button>
              )}
            />
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper variant="outlined" className={classes.mainPaper}>
        {boardLoading && (
          <div className={classes.loadingOverlay}>
            <div className={classes.loadingCard}>
              <div className={classes.progressHeader}>
                <Typography className={classes.loadingText}>Carregando Kanban...</Typography>
                <Typography className={classes.progressPercent}>{loadingProgress.percent}%</Typography>
              </div>
              <div className={classes.progressTrack}>
                <div
                  className={classes.progressFill}
                  style={{ width: `${loadingProgress.percent}%` }}
                />
              </div>
              <Typography className={classes.loadingMeta}>
                {loadingProgress.total
                  ? `${loadingProgress.loaded.toLocaleString('pt-BR')} / ${loadingProgress.total.toLocaleString('pt-BR')} tickets`
                  : `${loadingProgress.loaded.toLocaleString('pt-BR')} tickets carregados`}
              </Typography>
            </div>
          </div>
        )}
        <KanbanBoard
          lanes={lanes}
          onCardMove={handleCardMove}
          onLaneReorder={handleLaneReorder}
          updateTicket={updateTicket}
          onTicketClosed={onTicketClosed}
          onTicketTransferred={handleTicketTransferred}
          isAdmin={user.profile === 'admin'}
        />
      </Paper>
    </MainContainer>
  );
};

export default Kanban;
